/**
 * The approver's queue — Resource Plan RP-17.2, M4.
 *
 * ──────────────────────────────────────────────────────────────────────────────
 * THIS IS THE "IN-APP NOTIFICATION" HALF OF RP-17.2
 * ──────────────────────────────────────────────────────────────────────────────
 * RP-17.2: "Approvers are notified in-app and by email (Microsoft Foundation)." There is no
 * mail transport and no notification system in this repo — no Graph client, no queue, no
 * `notification` table — so the email half is a Foundation integration and is recorded as
 * outstanding in the M4 handoff rather than faked.
 *
 * This page is the in-app half, and arguably the better one: a queue is durable and
 * re-readable, where a notification is a one-shot interruption that an approver
 * checking on Monday morning has already lost. It is worked oldest-first with an age column,
 * the same shape as RP-15.3's "Handed over, not staffed" alert card.
 *
 * 🔒 Gated on `viewPlanCost` server-side, because every row carries a shortfall in dollars.
 * `[ACCT]` cannot reach it at all, which is why it is a route rather than a tab on a plan.
 */

import { Link } from "@tanstack/react-router";
import { CheckCircle2, ExternalLink, Inbox } from "lucide-react";
import { ErrorState } from "@/components/shared/error-state";
import { PageContainer } from "@/components/shared/PageContainer";
import { PageHeader } from "@/components/shared/PageHeader";
import { Skeleton } from "@/components/ui/skeleton";
import { useApprovalQueue } from "@/hooks/use-plan-governance";
import { formatPlanMoney } from "@/lib/plan-format";
import { cn } from "@/lib/utils";

function when(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function ApprovalsQueuePage() {
  const { data, isLoading, isError, error, refetch } = useApprovalQueue();

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Manage"
        title="Plan approvals"
        description="Plans held Open because their margin of record is below the floor, or because a Plan Line is priced below its cost. Each one is waiting on a decision from Finance or an Admin before it can be Proposed."
        actions={
          <Link
            to="/dashboard/plans"
            className="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
          >
            All plans
            <ExternalLink className="h-3 w-3" />
          </Link>
        }
      />

      {isLoading && <Skeleton className="h-64 w-full" />}

      {isError && (
        <ErrorState
          message={error instanceof Error ? error.message : "Could not load the approval queue."}
          onRetry={refetch}
        />
      )}

      {data !== undefined && data.length === 0 && (
        <div className="rounded-lg border border-dashed border-border px-4 py-12 text-center">
          <CheckCircle2 className="mx-auto h-6 w-6 text-success-700 dark:text-success-400" />
          <p className="mt-2 text-sm font-medium">Nothing waiting</p>
          <p className="mx-auto mt-1 max-w-md text-sm leading-relaxed text-muted-foreground">
            No plan is currently held for approval. A plan appears here the moment a planner tries
            to propose one below the floor, or a change takes a sent plan below it.
          </p>
        </div>
      )}

      {data !== undefined && data.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[980px] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Plan</th>
                <th className="px-3 py-2 font-medium">Account</th>
                <th className="px-3 py-2 font-medium">Requested</th>
                <th className="px-3 py-2 font-medium">By</th>
                <th className="px-3 py-2 text-right font-medium">Age</th>
                <th className="px-3 py-2 text-right font-medium">Margin</th>
                <th className="px-3 py-2 text-right font-medium">Floor</th>
                <th className="px-3 py-2 text-right font-medium">Shortfall</th>
                <th className="px-3 py-2 font-medium">Why</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <tr key={row.id} className="border-b border-border last:border-b-0">
                  <td className="px-3 py-2">
                    <Link
                      to="/dashboard/plans/$planId"
                      params={{ planId: row.resourcePlanId }}
                      className="font-medium underline-offset-2 hover:underline"
                    >
                      {row.planName}
                    </Link>
                    <span className="ml-1.5 font-mono text-xs text-muted-foreground">
                      v{row.versionNumber}
                    </span>
                    {row.origin === "Edit" && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Raised by a change after it was sent
                      </p>
                    )}
                    {row.requestedNote !== null && (
                      <p className="mt-0.5 max-w-[260px] text-xs leading-relaxed text-muted-foreground">
                        “{row.requestedNote}”
                      </p>
                    )}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {row.clientAccountName}
                    <span className="block text-xs">{row.solutionName}</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
                    {when(row.requestedAt)}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{row.requestedByName ?? "—"}</td>
                  <td className="px-3 py-2 text-right font-mono">
                    <span
                      className={cn(
                        row.ageDays >= 3 && "font-semibold text-warning-700 dark:text-warning-400",
                      )}
                    >
                      {row.ageDays === 0 ? "today" : `${row.ageDays}d`}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {row.marginOfRecordPercent === null ? (
                      "—"
                    ) : (
                      <span
                        className={cn(
                          row.triggerBelowFloor && "text-error-700 dark:text-error-400",
                        )}
                      >
                        {row.marginOfRecordPercent}%
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-muted-foreground">
                    {row.marginFloorPercent}%
                  </td>
                  <td className="px-3 py-2 text-right font-mono">
                    {formatPlanMoney(row.shortfallAmount)}
                  </td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {row.triggerBelowFloor && (
                        <span className="rounded bg-error-100 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wider text-error-700 dark:text-error-400">
                          below floor
                        </span>
                      )}
                      {row.triggerBelowCostLineCount > 0 && (
                        <span className="rounded bg-warning-100 px-1.5 py-0.5 text-xs font-semibold uppercase tracking-wider text-warning-700 dark:text-warning-400">
                          {row.triggerBelowCostLineCount} below cost
                        </span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data !== undefined && data.length > 0 && (
        <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Inbox className="h-3.5 w-3.5" />
          Open a plan and use its Approval tab to decide. Approving freezes the version and moves
          the plan to Proposed.
        </p>
      )}
    </PageContainer>
  );
}
